import React from "react";
import { useDashboardSummary, useActiveAlerts, useAirQualityHotspots, useGlobalWeather } from "../hooks/useData";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const AQI_COLORS = {
  good: "#10B981",
  moderate: "#FBBF24",
  unhealthy_sensitive: "#F97316",
  unhealthy: "#F97316",
  very_unhealthy: "#EF4444",
  hazardous: "#FF4444",
  unknown: "#7A8EA8",
};

function StatCard({ label, value, sub, color, loading }) {
  return (
    <div className="card fade-in" style={{ flex: 1 }}>
      <div className="card-title">{label}</div>
      {loading ? (
        <div className="skeleton" style={{ height: 36, width: "60%", marginBottom: 6 }} />
      ) : (
        <div style={{ fontSize: 32, fontFamily: "var(--mono)", fontWeight: 700, color: color || "var(--text)" }}>
          {value ?? "—"}
        </div>
      )}
      {sub && <div style={{ fontSize: 11, color: "var(--subtext)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function AlertRow({ alert }) {
  const colors = { critical: "#FF4444", high: "#F97316", medium: "#FBBF24", low: "#10B981" };
  const color = colors[alert.severity] || "var(--subtext)";
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 12,
      padding: "10px 0", borderBottom: "1px solid var(--border)"
    }}>
      <span style={{ color, fontFamily: "var(--mono)", fontSize: 10, marginTop: 2, flexShrink: 0 }}>
        {alert.severity?.toUpperCase()}
      </span>
      <div>
        <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.4 }}>{alert.message}</div>
        <div style={{ fontSize: 10, color: "var(--subtext)", marginTop: 2 }}>{alert.type} · {alert.country || alert.location}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data: summary, isLoading: loadingSum } = useDashboardSummary();
  const { data: alerts, isLoading: loadingAlerts } = useActiveAlerts();
  const { data: hotspots, isLoading: loadingHotspots } = useAirQualityHotspots("pm25", 8);
  const { data: weather, isLoading: loadingWeather } = useGlobalWeather();

  const topAlerts = alerts?.alerts?.slice(0, 6) || [];
  const hotspotList = hotspots?.hotspots || [];
  const cityWeather = weather?.cities?.slice(0, 8) || [];

  const pm25Color = AQI_COLORS[summary?.pm25_category] || "var(--accent)";

  return (
    <div className="fade-in">
      <h1 style={{ fontFamily: "var(--mono)", fontSize: 20, marginBottom: 6 }}>OVERVIEW</h1>
      <p style={{ color: "var(--subtext)", fontSize: 12, marginBottom: 24 }}>
        Global environmental intelligence · Live aggregated data
      </p>

      {/* KPI Row */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
        <StatCard
          label="PM2.5 Global Avg"
          value={summary?.pm25_avg != null ? `${summary.pm25_avg} µg/m³` : "—"}
          sub={summary?.pm25_category?.replace(/_/g, " ") || "loading..."}
          color={pm25Color}
          loading={loadingSum}
        />
        <StatCard
          label="Active Alerts"
          value={summary?.total_alerts ?? "—"}
          sub="All severity levels"
          color="var(--accent2)"
          loading={loadingSum}
        />
        <StatCard
          label="Critical Alerts"
          value={summary?.critical_alerts ?? "—"}
          sub="Requires immediate attention"
          color="#FF4444"
          loading={loadingSum}
        />
        <StatCard
          label="Last Updated"
          value={summary?.last_updated ? new Date(summary.last_updated).toLocaleTimeString() : "—"}
          sub="UTC"
          loading={loadingSum}
        />
      </div>

      {/* Main Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* PM2.5 Hotspots */}
        <div className="card">
          <div className="card-title">PM2.5 Hotspots · Global</div>
          {loadingHotspots ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 24, marginBottom: 8 }} />
            ))
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={hotspotList} layout="vertical" barSize={12}>
                <XAxis type="number" tick={{ fill: "var(--subtext)", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="city"
                  width={80}
                  tick={{ fill: "var(--subtext)", fontSize: 10 }}
                  axisLine={false} tickLine={false}
                  tickFormatter={v => v?.slice(0, 12) || "Unknown"}
                />
                <Tooltip
                  contentStyle={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11 }}
                  formatter={(v) => [`${v} µg/m³`, "PM2.5"]}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {hotspotList.map((h, i) => (
                    <Cell key={i} fill={AQI_COLORS[h.aqi_category] || AQI_COLORS.unknown} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Active Alerts */}
        <div className="card">
          <div className="card-title">Active Alerts · {alerts?.count || 0} total</div>
          {loadingAlerts ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8 }} />
            ))
          ) : topAlerts.length > 0 ? (
            topAlerts.map((a, i) => <AlertRow key={i} alert={a} />)
          ) : (
            <div style={{ color: "var(--success)", fontSize: 13, padding: "20px 0" }}>
              ✓ No active alerts
            </div>
          )}
        </div>
      </div>

      {/* Weather Strip */}
      <div className="card">
        <div className="card-title">Global City Temperatures</div>
        {loadingWeather ? (
          <div className="skeleton" style={{ height: 60 }} />
        ) : (
          <div style={{ display: "flex", gap: 16, overflowX: "auto", paddingBottom: 4 }}>
            {cityWeather.map((c, i) => (
              <div key={i} style={{
                flexShrink: 0, textAlign: "center",
                padding: "8px 14px",
                background: "var(--surface2)",
                borderRadius: 8,
                minWidth: 80,
              }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: 16, fontWeight: 700, color: "var(--accent2)" }}>
                  {c.temp_c?.toFixed(1)}°
                </div>
                <div style={{ fontSize: 10, color: "var(--subtext)", marginTop: 2 }}>{c.city}</div>
                <div style={{ fontSize: 9, color: "var(--subtext)", opacity: 0.7 }}>{c.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
